import {GetFilteredRestResource, GetRestResource} from "../../api/RestResourceClient";

//topics of the advanced search; a topic searches for products with at least one of its tags;
//the tags are lowercase, because the tag filter of the search compares with the lowercased words of the tags
export const ADVANCED_SEARCH_TOPICS = [
    {key: 'DETECTIVE', labelKey: 'SEARCH_TOPIC_DETECTIVE', tags: ['detektive', 'krimi']},
    {key: 'KNOWLEDGE', labelKey: 'SEARCH_TOPIC_KNOWLEDGE',
        tags: ['wissen', 'wissenschaften', 'chemie', 'physik', 'mathematik', 'biologie', 'astronomie', 'geografie']},
    {key: 'HORSES', labelKey: 'SEARCH_TOPIC_HORSES', tags: ['pferde', 'ponys', 'pferdehof', 'hobby-horsing', 'reiten']},
    {key: 'SPORTS', labelKey: 'SEARCH_TOPIC_SPORTS',
        tags: ['fussball', 'sport', 'fussballsongs', 'eishockey', 'wasserball', 'tennis', 'american football', 'basketball',
            'minigolf', 'golfen', 'handball', 'turnen', 'baseball']},
    {key: 'SLEEP', labelKey: 'SEARCH_TOPIC_SLEEP', tags: ['einschlafen', 'gute-nacht-geschichte']},
    {key: 'ADVENTURE', labelKey: 'SEARCH_TOPIC_ADVENTURE', tags: ['abenteuer']},
    {key: 'FANTASY_JOURNEYS', labelKey: 'SEARCH_TOPIC_FANTASY_JOURNEYS', tags: ['fantasiereisen', 'traumreisen']},
    {key: 'CHRISTMAS', labelKey: 'SEARCH_TOPIC_CHRISTMAS',
        tags: ['advent', 'weihnachten', 'englische weihnachtslieder', 'weihnachtsmusik', 'nikolaus']},
    {key: 'HALLOWEEN', labelKey: 'SEARCH_TOPIC_HALLOWEEN', tags: ['grusel', 'halloween', 'spuk']},
    {key: 'FANTASY', labelKey: 'SEARCH_TOPIC_FANTASY', tags: ['romantasy', 'fantasy']},
];

//ISO3 language codes, see media.tiger.audiothek.product.ProductResourceProcessor;
//browserLanguage is the ISO 639-1 code used by the browser for this language
export const ADVANCED_SEARCH_LANGUAGES = [
    {value: 'deu', labelKey: 'LANGUAGE_DEU', browserLanguage: 'de'},
    {value: 'gsw', labelKey: 'LANGUAGE_GSW', browserLanguage: 'gsw'},
    {value: 'eng', labelKey: 'LANGUAGE_ENG', browserLanguage: 'en'},
    {value: 'tur', labelKey: 'LANGUAGE_TUR', browserLanguage: 'tr'},
    {value: 'spa', labelKey: 'LANGUAGE_SPA', browserLanguage: 'es'},
    {value: 'fra', labelKey: 'LANGUAGE_FRA', browserLanguage: 'fr'},
    {value: 'swe', labelKey: 'LANGUAGE_SWE', browserLanguage: 'sv'},
    {value: 'fin', labelKey: 'LANGUAGE_FIN', browserLanguage: 'fi'},
    {value: 'ita', labelKey: 'LANGUAGE_ITA', browserLanguage: 'it'},
    {value: 'nld', labelKey: 'LANGUAGE_NLD', browserLanguage: 'nl'},
];

function getBrowserLanguages() {
    const languages = navigator.languages && navigator.languages.length > 0 ? navigator.languages : [navigator.language];
    return languages.filter(language => language).map(language => language.toLowerCase());
}

//a user is probably from Switzerland if one of the browser languages is a Swiss one (e.g. de-CH, fr-CH, gsw)
//or the time zone of the browser is the Swiss one
function isProbablySwissUser(browserLanguages) {
    let timeZone = "";
    try {
        timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch (e) {
        //without time zone only the browser languages are used
    }
    return browserLanguages.some(language => language.endsWith("-ch") || language.split("-")[0] === "gsw")
            || timeZone === "Europe/Zurich";
}

//the languages ranked higher in the search if no language is selected: the language of the browser if it's one of the
//offered languages, otherwise none; users from Switzerland get German, Swiss German and their browser language
//(e.g. French for fr-CH), Turkish speaking users German and Turkish
export function getPreferredLanguages() {
    const browserLanguages = getBrowserLanguages();
    const browserLanguage = (browserLanguages[0] || "").split("-")[0];
    const language = ADVANCED_SEARCH_LANGUAGES.find(l => l.browserLanguage === browserLanguage);
    if (isProbablySwissUser(browserLanguages)) {
        const otherLanguage = language && language.value !== 'deu' && language.value !== 'gsw' ? [language.value] : [];
        return ['deu', ...otherLanguage, 'gsw'];
    }
    if (browserLanguage === "tr") {
        return ['deu', 'tur'];
    }
    return language ? [language.value] : [];
}

//the tags of all selected topics, sent to the search as repeated parameter "tags"
export function getTagsOfTopics(topics) {
    return [...new Set(ADVANCED_SEARCH_TOPICS
            .filter(topic => topics && topics.includes(topic.key))
            .flatMap(topic => topic.tags))];
}

//without selected languages the search finds products and series in all languages, but ranks the ones in the
//preferred languages higher; with selected languages only these are found, so nothing is preferred
function getPreferredLanguageParameter(languages) {
    return languages && languages.length > 0 ? null : getPreferredLanguages().join(",") || null;
}

//API METHODS
export async function GetFilteredProducts(filter) {
    const {topics, ...searchFilter} = filter;
    //createFilterQuery prefixes array values with a comma, so the languages are sent as a plain comma separated string
    const languages = Array.isArray(filter.language) && filter.language.length > 0 ? filter.language.join(",") : null;
    //the tags of the selected topics are sent as repeated parameter (tags=a&tags=b), a tag may contain spaces
    const tags = getTagsOfTopics(topics);
    const url = 'api/searches/products-by-customer'
            + (tags.length > 0 ? '?' + tags.map(tag => 'tags=' + encodeURIComponent(tag)).join('&') : '');
    return await GetFilteredRestResource(url, {...searchFilter, language: languages,
            preferredLanguage: getPreferredLanguageParameter(filter.language)}, null, false, false, false, false);
}

//suggestions of series and products for the search field, see SearchInput
//ageMin/ageMax: the age filter of the search, so that only suggestions matching the search results are shown
export async function GetSearchSuggestions(text, languages, ageMin, ageMax) {
    const filter = {
        text: text,
        language: languages && languages.length > 0 ? languages.join(",") : null,
        ageMin: ageMin,
        ageMax: ageMax,
        preferredLanguage: getPreferredLanguageParameter(languages)
    };
    return await GetFilteredRestResource('api/searches/suggest-product-search-terms', filter, null, false, false,
            false, false);
}

export async function GetStartpage(language) {
    return await GetRestResource('api/searches/start-page' + (language ? "?language=" + language : ""), false, true);
}

//a playlist has no length of its own, its length is the sum of the durations (in seconds) of its tracks
function getPlaylistLength(product) {
    const playlistItems = product._embedded?.playlistItems;
    if (product.productClassification !== "PLAYLIST" || !playlistItems || playlistItems.length === 0) {
        return null;
    }
    return playlistItems.reduce((sum, item) => sum + (item.duration > 0 ? item.duration : 0), 0);
}

export function getProductLength(product) {
    if (product.productType === "MOVIE" || product.productType === "AUDIOBOOK") {
        const seconds = product.attributes && product.attributes.length ? product.attributes.length : getPlaylistLength(product);
        if (seconds) {
            const minutes = Math.floor(seconds / 60);

            return '<span>' + minutes + "</span> Min";
        } else {
            return null;
        }
    } else {
        return null;
    }
}

